/**
 * FAIL-CLOSED OMGEVINGSGRENDEL — hoort deze app, op dit origin, bij dit
 * Supabase-project?
 *
 * AANLEIDING (01-09-2026, forensisch onderzoek layer0@cadobv.be). Onder
 * `https://portal.collectr.be` draaide een VEROUDERD artefact van de Operator
 * App dat bedraad was op het DEV-project. Het domein suggereerde productie, de
 * schrijfacties landden op development, en niets in de applicatie merkte het op.
 * Gemeten: 248 van de 287 auth-requests op collectr-dev in 24 uur droegen
 * referer `https://portal.collectr.be`, terwijl de dan gedeployde portal met
 * productie sprak en helemaal geen tweefactorcode bevat.
 *
 * Twee dingen worden daarom vastgeklonken, niet één:
 *  1. WELKE APP mag onder dit origin draaien (een Operator App onder het
 *     portaaldomein is op zichzelf al fout, ongeacht het project);
 *  2. WELK Supabase-project die combinatie mag gebruiken.
 *
 * Zuivere functie: geen `window`, geen `import.meta`, geen netwerk. De aanroeper
 * levert de drie feiten aan. Alles wat niet expliciet in de tabel staat, wordt
 * geweigerd — er is bewust geen brede wildcard, want een grendel die alles
 * doorlaat is geen grendel.
 */

export type AppIdentiteit =
  | "operator"
  | "opdrachtgeverportaal"
  | "debiteurportaal"
  | "deurwaardersportaal";

export type Projectnaam = "collectr-dev" | "collectr-prod" | "collectr-isolated-preview";

export const PROJECTEN: Record<string, Projectnaam> = {
  wepiawwkninwzkbhypzj: "collectr-dev",
  qnsfotdhlxkbmqfapvmu: "collectr-prod",
};

/** Cloudflare Pages-project per applicatie; bepaalt het toegestane previewhost. */
const PAGES_PROJECT: Record<AppIdentiteit, string | null> = {
  operator: "collectr-app",
  opdrachtgeverportaal: "collectr-portal",
  debiteurportaal: "collectr-debtor-portal",
  deurwaardersportaal: null, // nog niet gedeployd; previews dus ook niet toegestaan
};

/** Vaste domeinen. Eén host hoort bij precies één app en één project. */
const PRODUCTIEHOSTS: Record<string, { app: AppIdentiteit; project: Projectnaam }> = {
  "app.collectr.be": { app: "operator", project: "collectr-prod" },
  "collectr-app.pages.dev": { app: "operator", project: "collectr-prod" },
  "portal.collectr.be": { app: "opdrachtgeverportaal", project: "collectr-prod" },
  "inbox-test.collectr.be": { app: "opdrachtgeverportaal", project: "collectr-dev" },
  "collectr-portal.pages.dev": { app: "opdrachtgeverportaal", project: "collectr-prod" },
  "dossier.collectr.be": { app: "debiteurportaal", project: "collectr-prod" },
  "collectr-debtor-portal.pages.dev": { app: "debiteurportaal", project: "collectr-prod" },
};

const LOKALE_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]", "::1"]);

export type Grendelbevinding =
  | { toegestaan: true; app: AppIdentiteit; project: Projectnaam; host: string }
  | {
      toegestaan: false;
      code:
        | "ONBEKEND_ORIGIN"
        | "ONBEKEND_PROJECT"
        | "VERKEERDE_APP"
        | "VERKEERD_PROJECT";
      uitleg: string;
      app: AppIdentiteit;
      host: string;
      /** Altijd gemaskeerd. Een projectreferentie is geen sleutel, maar hoort
       *  ook niet volledig op een foutscherm van een willekeurige bezoeker. */
      projectGemaskeerd: string;
    };

/** `wepiawwkninwzkbhypzj` → `wepi…hypzj`. Nooit de volledige referentie tonen. */
export function maskeerProject(ref: string): string {
  if (ref.length <= 9) return ref;
  return `${ref.slice(0, 4)}…${ref.slice(-5)}`;
}

/** Haalt de projectreferentie uit een Supabase-URL. Geeft null bij elke andere vorm. */
export function projectReferentieUit(supabaseUrl: string | null | undefined): string | null {
  if (!supabaseUrl) return null;
  let host: string;
  try {
    host = new URL(supabaseUrl).hostname;
  } catch {
    return null;
  }
  const m = /^([a-z0-9]{20})\.supabase\.(co|red)$/.exec(host);
  return m?.[1] ?? null;
}

function hostVan(origin: string): string | null {
  try {
    return new URL(origin).hostname;
  } catch {
    return null;
  }
}

/**
 * Is dit een previewhost van precies dít Pages-project?
 *
 * Toegestaan als preview (development): `<deploy-label>.collectr-app.pages.dev`.
 * Het kale `collectr-app.pages.dev` is de productie-alias, geen preview.
 * Bewust NIET: `*.pages.dev` in het algemeen — dat zou elk Pages-project van
 * iedereen doorlaten en de controle betekenisloos maken.
 */
function isPreviewVan(host: string, pagesProject: string | null): boolean {
  if (pagesProject === null) return false;
  /* Het kale `<project>.pages.dev` is de productie-alias, geen preview. */
  if (host === `${pagesProject}.pages.dev`) return false;
  const achtervoegsel = `.${pagesProject}.pages.dev`;
  if (!host.endsWith(achtervoegsel)) return false;
  const label = host.slice(0, -achtervoegsel.length);
  return label.length > 0 && !label.includes(".") && /^[a-z0-9-]+$/.test(label);
}

export function beoordeelOmgeving(invoer: {
  app: AppIdentiteit;
  origin: string;
  supabaseUrl: string | null | undefined;
  /** Alleen door de Vite-developmentboot aangeleverd; nooit een productieoverride. */
  isolatedPreviewProjectRef?: string;
}): Grendelbevinding {
  const { app, origin } = invoer;
  const host = hostVan(origin) ?? "(onbekend)";
  const ref = projectReferentieUit(invoer.supabaseUrl);
  const gemaskeerd = ref ? maskeerProject(ref) : "(geen)";

  // Expliciete native lokale testbackend; deze waarde komt alleen uit de DEV-boot.
  // Beide kanten moeten loopback zijn. Hosted DEV/LIVE kan nooit hieronder vallen.
  if (app === "operator" && LOKALE_HOSTS.has(host) && invoer.isolatedPreviewProjectRef === "local" &&
      invoer.supabaseUrl && LOKALE_HOSTS.has(hostVan(invoer.supabaseUrl) ?? "")) {
    return { toegestaan: true, app, project: "collectr-isolated-preview", host };
  }

  // Expliciet gekoppelde lokale Operator-testbackend. Bestaande DEV/LIVE-paden
  // blijven hieronder ongewijzigd; een override mag ze nooit herclassificeren.
  if (app === "operator" && LOKALE_HOSTS.has(host) && ref !== null &&
      PROJECTEN[ref] === undefined && ref === invoer.isolatedPreviewProjectRef) {
    return { toegestaan: true, app, project: "collectr-isolated-preview", host };
  }

  const project = ref === null ? undefined : PROJECTEN[ref];
  if (ref === null || project === undefined) {
    return {
      toegestaan: false,
      code: "ONBEKEND_PROJECT",
      uitleg: "De geconfigureerde Supabase-URL hoort bij geen enkel bekend Collectr-project.",
      app,
      host,
      projectGemaskeerd: gemaskeerd,
    };
  }

  /* 1 — vast productiedomein: app én project liggen vast. */
  const productie = PRODUCTIEHOSTS[host];
  if (productie) {
    if (productie.app !== app) {
      return {
        toegestaan: false,
        code: "VERKEERDE_APP",
        uitleg: `Onder ${host} hoort ${omschrijf(productie.app)} te draaien, niet ${omschrijf(app)}.`,
        app,
        host,
        projectGemaskeerd: gemaskeerd,
      };
    }
    if (productie.project !== project) {
      return {
        toegestaan: false,
        code: "VERKEERD_PROJECT",
        uitleg: `Onder ${host} is uitsluitend ${productie.project} toegestaan.`,
        app,
        host,
        projectGemaskeerd: gemaskeerd,
      };
    }
    return { toegestaan: true, app, project, host };
  }

  /* 2 — lokale ontwikkeling: alleen het devproject. */
  if (LOKALE_HOSTS.has(host)) {
    if (project !== "collectr-dev") {
      return {
        toegestaan: false,
        code: "VERKEERD_PROJECT",
        uitleg:
          "Lokaal draaien mag uitsluitend tegen collectr-dev. Een lokale build tegen productie is nooit de bedoeling.",
        app,
        host,
        projectGemaskeerd: gemaskeerd,
      };
    }
    return { toegestaan: true, app, project, host };
  }

  /* 3 — previewdeployment van precies dit Pages-project: uitsluitend development.
   * Het kale `<project>.pages.dev` is de productie-alias en valt onder
   * PRODUCTIEHOSTS. `main.<project>.pages.dev` en overige branchpreviews zijn
   * de gehoste ontwikkelomgeving — dezelfde backend als localhost. */
  if (isPreviewVan(host, PAGES_PROJECT[app])) {
    if (project !== "collectr-dev") {
      return {
        toegestaan: false,
        code: "VERKEERD_PROJECT",
        uitleg: `Een Pages-preview van ${omschrijf(app)} mag uitsluitend collectr-dev gebruiken.`,
        app,
        host,
        projectGemaskeerd: gemaskeerd,
      };
    }
    return { toegestaan: true, app, project, host };
  }

  return {
    toegestaan: false,
    code: "ONBEKEND_ORIGIN",
    uitleg: `${host} is geen bekend Collectr-domein voor ${omschrijf(app)}.`,
    app,
    host,
    projectGemaskeerd: gemaskeerd,
  };
}

export function omschrijf(app: AppIdentiteit): string {
  return {
    operator: "de Operator App",
    opdrachtgeverportaal: "het Opdrachtgeversportaal",
    debiteurportaal: "het Debiteurportaal",
    deurwaardersportaal: "het Deurwaardersportaal",
  }[app];
}
